import React, { useState } from "react";
import KuikaButton from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaMenuItem from "../../../../ui-kit/kuika-menu-item/kuika-menu-item";
import KuikaSelectionIndicator from "../../../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";

interface IMenuGroupProps {
  children: React.ReactNode;
}

const Group: React.FC<IMenuGroupProps> = ({ children }: IMenuGroupProps) => (
  <div style={{ width: 280, border: "1px solid var(--lab-border)", background: "var(--lab-paper)" }}>{children}</div>
);

const SLIDING_ROWS: string[] = ["Home", "Customers", "Orders", "Invoices", "Settings"];

const SlidingSelection: React.FC = (): JSX.Element => {
  const [selected, setSelected] = useState<string>(SLIDING_ROWS[0]);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--gap-s)", alignItems: "flex-start" }}>
      <Group>
        <div className="kuika-selection-indicator-host--menu-items">
          <KuikaSelectionIndicator hidden={isDragging} />
          {SLIDING_ROWS.map((title: string): JSX.Element => (
            <KuikaMenuItem
              key={title}
              title={title}
              leftIcon={<KuikaSvg name="file" width={14} height={14} />}
              selected={title === selected}
              onClick={(): void => setSelected(title)}
            />
          ))}
        </div>
      </Group>
      <KuikaButton type="tertiary" size="sm" onClick={(): void => setIsDragging(!isDragging)}>
        {isDragging ? "End drag" : "Start drag (hides the indicator)"}
      </KuikaButton>
    </div>
  );
};

type CollapseKey = string | number;

const FOLDER_KEY: string = "invoices";
const FOLDER_ROWS: string[] = ["Invoice detail", "Invoice list"];
const ROOT_ROWS: string[] = ["Dashboard", "Customers", "Settings"];

/**
 * The list is ONE Tab stop — the selected row — and the arrows move inside it, folder rows
 * included: `useRovingFocus` groups the rows by their list container, and a folder's collapse only
 * nests them. Collapse the folder and its rows drop out of the walk.
 */
const KeyboardList: React.FC = (): JSX.Element => {
  const [selected, setSelected] = useState<string>("Customers");
  const [openFolders, setOpenFolders] = useState<CollapseKey[]>([FOLDER_KEY]);

  const renderRow = (title: string): JSX.Element => (
    <KuikaMenuItem
      key={title}
      title={title}
      leftIcon={<KuikaSvg name="file" width={14} height={14} />}
      selected={title === selected}
      onClick={(): void => setSelected(title)}
    />
  );

  return (
    <Group>
      <div className="kuika-selection-indicator-host--menu-items">
        <KuikaSelectionIndicator />
        <KuikaCollapsePanel
          activeKey={openFolders}
          onChange={(keys: CollapseKey[]): void => setOpenFolders(keys)}
          items={[
            {
              key: FOLDER_KEY,
              label: (
                <span style={{ display: "inline-flex", gap: "var(--gap-xs)", alignItems: "center" }}>
                  <KuikaSvg name="folder" type="sidebar-icons" width={14} height={14} />
                  Invoices
                </span>
              ),
              children: <>{FOLDER_ROWS.map(renderRow)}</>
            }
          ]}
        />
        {ROOT_ROWS.map(renderRow)}
      </div>
    </Group>
  );
};

export const MenuItemStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="Menu Item"
      componentName="KuikaMenuItem"
      sourcePath="ui-kit/kuika-menu-item"
      description="The single row primitive used inside management left panels and dropdowns. Supports icons, units, selected state and a trailing dropdown slot."
    >
      <SpecRow label="States" caption="default · selected · with right unit" align="start">
        <Group>
          <KuikaMenuItem title="Dashboard" leftIcon={<KuikaSvg name="dashboard" width={14} height={14} />} />
          <KuikaMenuItem title="Projects" leftIcon={<KuikaSvg name="folder" width={14} height={14} />} selected />
          <KuikaMenuItem title="Logs" leftIcon={<KuikaSvg name="logs" width={14} height={14} />} rightUnit="12" />
        </Group>
      </SpecRow>
      <SpecRow label="Without icon" align="start">
        <Group>
          <KuikaMenuItem title="Recent" />
          <KuikaMenuItem title="Favorites" selected />
          <KuikaMenuItem title="Archive" />
        </Group>
      </SpecRow>
      <SpecRow
        label="Sliding selection"
        caption="KuikaSelectionIndicator — one fill slides to the clicked row; hidden while a row is dragged"
        align="start"
      >
        <SlidingSelection />
      </SpecRow>
      <SpecRow
        label="Keyboard"
        caption="One Tab stop per list: the selected row. ↑/↓ walk the rows, Home/End jump to the ends, Enter or Space opens one. A folder's rows belong to the same list — closing the folder takes them out of the walk"
        align="start"
      >
        <KeyboardList />
      </SpecRow>
      <SpecRow label="With trailing dropdown" caption="rightDropdown slot — context actions" align="start">
        <Group>
          <KuikaMenuItem
            title="Production app"
            leftIcon={<KuikaSvg name="rocket" width={14} height={14} />}
            rightDropdown={<KuikaSvg name="more-horizontal" width={14} height={14} />}
          />
          <KuikaMenuItem
            title="Staging app"
            leftIcon={<KuikaSvg name="rocket" width={14} height={14} />}
            rightDropdown={<KuikaSvg name="more-horizontal" width={14} height={14} />}
          />
        </Group>
      </SpecRow>
      <SpecRow
        label="Static"
        caption="interactive={false} — a row that only names something (the right panel's header naming the selection): no hover fill, no brightening, no pointer. Controls inside it keep their own hover"
        align="start"
      >
        <Group>
          <KuikaMenuItem
            title="Customer name"
            leftIcon={<KuikaSvg name="file" width={14} height={14} />}
            rightUnit={
              <KuikaButton type="tertiary" size="sm" isIcon aria-label="Element actions">
                <KuikaSvg name="dots" width={16} height={16} />
              </KuikaButton>
            }
            interactive={false}
          />
        </Group>
      </SpecRow>
    </ShowcaseFrame>
  );
};
